import React, { useState } from 'react';

import { IdeaFieldTypes } from 'common/constants/ideas';
import ModernModal from 'common/modals/ModernModal';
import {
  type FieldFormState,
  type FieldSubmission,
  type FormulaFieldFormState,
  type ScoreFieldFormState,
  useIdeaFieldForm,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import Button from 'common/uiV3/Button';
import styles from 'css-module/components/subdomain/admin/v2/_AdminV2CustomIdeaFieldModal.module.scss';

import BaseFieldInputs from './form/BaseFieldInputs';
import FormulaFieldInputs from './form/FormulaFieldInputs';
import ScoreFieldInputs from './form/ScoreFieldInputs';

import type { CustomIdeaField, FormulaIdeaField, ScoreIdeaField } from 'common/api/endpoints/idea';

type BaseFieldModalProps = {
  loading: boolean;
  onClose: () => void;
  onSubmit: (field: FieldSubmission) => void;
  types?: IdeaFieldTypes[];
};

type CreateFieldModalProps = BaseFieldModalProps & {
  mode: 'create';
  initialData?: never;
};

type UpdateFieldModalProps = BaseFieldModalProps & {
  mode: 'update';
  initialData: CustomIdeaField | FormulaIdeaField | ScoreIdeaField;
  onDelete: () => void;
};

type Props = CreateFieldModalProps | UpdateFieldModalProps;

const isFormulaField = (formState: FieldFormState): formState is FormulaFieldFormState =>
  formState.type === IdeaFieldTypes.formula;

const isScoreField = (formState: FieldFormState): formState is ScoreFieldFormState =>
  formState.type === IdeaFieldTypes.score;

export default function AdminV2CustomIdeaFieldModal(props: Props) {
  const { onClose, loading, mode, onSubmit, initialData, types } = props;

  const { formState, isValid, handleSubmit, setValue } = useIdeaFieldForm(initialData);

  // we need to keep track of any modals open on top of this modal,
  // so we can hide the overlay when they are open, to avoid the double overlay effect.
  const [isOverlayModalOpen, setIsOverlayModalOpen] = useState(false);

  const handleDelete = () => {
    if (loading || mode !== 'update') {
      return;
    }

    props.onDelete();
  };

  const headerText = mode === 'create' ? 'Create new field' : 'Edit field';
  return (
    <ModernModal
      // we need to prevent the modal from closing, because when creating or editing a formula field
      // we want to support opening the segment creation modal without closing this modal.
      closeOnClickAway={false}
      modalClassName={styles.createFieldModal}
      overlayClassName={isOverlayModalOpen ? styles.hideModalOverlay : undefined}
      header={headerText}
      onClose={onClose}
      sections={[
        <div key="main" className={styles.modalContent}>
          <BaseFieldInputs
            types={types}
            loading={loading}
            formState={formState}
            readOnly={mode === 'update'}
            setValue={setValue}
          />

          {isFormulaField(formState) && (
            <FormulaFieldInputs
              formulaFieldState={formState}
              readOnly={mode === 'update'}
              openOverlayModal={() => setIsOverlayModalOpen(true)}
              closeOverlayModal={() => setIsOverlayModalOpen(false)}
              setValue={setValue}
            />
          )}

          {isScoreField(formState) && (
            <ScoreFieldInputs scoreFieldState={formState} setValue={setValue} />
          )}
        </div>,
      ]}
      footer={
        <>
          {mode === 'update' ? (
            <Button
              color="error"
              disabled={loading}
              variant="outlined"
              className="mr-auto"
              onClick={handleDelete}>
              Delete
            </Button>
          ) : null}
          <Button disabled={loading} variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!isValid} onClick={() => handleSubmit(onSubmit)} isLoading={loading}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    />
  );
}
